import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import axios from 'axios';
import { Search, MapPin, MessageCircle, ExternalLink, Navigation, Loader2, X, Check, Send, ArrowRight, Users } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Community = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [allGroups, setAllGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [detectedCity, setDetectedCity] = useState(null);
  const [reqForm, setReqForm] = useState({ name: '', phone: '', city: '', state: '' });
  const [reqLoading, setReqLoading] = useState(false);
  const [reqSubmitted, setReqSubmitted] = useState(false);

  // Join-flow state
  const [joinTarget, setJoinTarget] = useState(null); // {city, group_name}
  const [joinForm, setJoinForm] = useState({ name: '', phone: '' });
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinResult, setJoinResult] = useState(null); // {group_link, city}

  const sampleGroups = [
    { city: 'Mumbai', state: 'Maharashtra', region: 'West', group_link: 'https://chat.whatsapp.com/sample1', group_name: 'Mumbai Seniors', member_count: 150 },
    { city: 'Delhi', state: 'Delhi', region: 'North', group_link: 'https://chat.whatsapp.com/sample2', group_name: 'Delhi Elders', member_count: 200 },
    { city: 'Bengaluru', state: 'Karnataka', region: 'South', group_link: 'https://chat.whatsapp.com/sample3', group_name: 'Bengaluru Seniors', member_count: 180 },
    { city: 'Chennai', state: 'Tamil Nadu', region: 'South', group_link: 'https://chat.whatsapp.com/sample4', group_name: 'Chennai Elders', member_count: 120 },
    { city: 'Kolkata', state: 'West Bengal', region: 'East', group_link: 'https://chat.whatsapp.com/sample5', group_name: 'Kolkata Seniors', member_count: 140 },
    { city: 'Hyderabad', state: 'Telangana', region: 'South', group_link: 'https://chat.whatsapp.com/sample6', group_name: 'Hyderabad Elders', member_count: 160 },
    { city: 'Pune', state: 'Maharashtra', region: 'West', group_link: 'https://chat.whatsapp.com/sample7', group_name: 'Pune Seniors', member_count: 130 },
    { city: 'Ahmedabad', state: 'Gujarat', region: 'West', group_link: 'https://chat.whatsapp.com/sample8', group_name: 'Ahmedabad Elders', member_count: 110 },
    { city: 'Jaipur', state: 'Rajasthan', region: 'North', group_link: 'https://chat.whatsapp.com/sample9', group_name: 'Jaipur Seniors', member_count: 90 },
    { city: 'Lucknow', state: 'Uttar Pradesh', region: 'North', group_link: 'https://chat.whatsapp.com/sample10', group_name: 'Lucknow Elders', member_count: 100 }
  ];

  useEffect(() => { fetchGroups(); }, []);

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const filtered = allGroups.filter(g =>
        g.city.toLowerCase().includes(term) ||
        g.state.toLowerCase().includes(term) ||
        g.group_name.toLowerCase().includes(term)
      );
      setFilteredGroups(filtered);
    } else {
      setFilteredGroups(allGroups);
    }
  }, [searchTerm, allGroups]);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/whatsapp-groups`);
      if (res.data && res.data.length > 0) {
        setAllGroups(res.data);
        setFilteredGroups(res.data);
      } else {
        setAllGroups(sampleGroups);
        setFilteredGroups(sampleGroups);
      }
    } catch {
      setAllGroups(sampleGroups);
      setFilteredGroups(sampleGroups);
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSearch = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          // Reverse geocode using OpenStreetMap Nominatim (free, no API key)
          const res = await axios.get(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`,
            { headers: { 'Accept-Language': 'en' } }
          );

          const addr = res.data?.address || {};
          // Nominatim returns city/town/village/county depending on location
          const city = addr.city || addr.town || addr.village || addr.county || addr.state_district || '';
          const state = addr.state || '';

          if (city) {
            setDetectedCity(city);
            setSearchTerm(city);
            toast.success(`Location detected: ${city}${state ? `, ${state}` : ''}`);
          } else {
            toast.error('Could not determine your city. Please search manually.');
          }
        } catch {
          toast.error('Failed to detect location. Please search manually.');
        } finally {
          setLocationLoading(false);
        }
      },
      (error) => {
        setLocationLoading(false);
        if (error.code === error.PERMISSION_DENIED) {
          toast.error('Location access denied. Please allow location access or search manually.');
        } else {
          toast.error('Unable to retrieve your location. Please search manually.');
        }
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  const clearLocation = () => {
    setDetectedCity(null);
    setSearchTerm('');
  };

  const openJoinModal = (group) => {
    setJoinTarget({ city: group.city, group_name: group.group_name });
    setJoinForm({ name: '', phone: '' });
    setJoinResult(null);
  };

  const closeJoinModal = (val) => {
    if (!val) {
      // Animate out before clearing
      setTimeout(() => {
        setJoinTarget(null);
        setJoinResult(null);
        setJoinForm({ name: '', phone: '' });
      }, 250);
    }
  };

  const handleJoinSubmit = async (e) => {
    e.preventDefault();
    const name = joinForm.name.trim();
    const phone = joinForm.phone.trim();
    if (!name || !phone) {
      toast.error('Please enter your name and phone number');
      return;
    }
    if (phone.replace(/\D/g, '').length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setJoinLoading(true);
    try {
      const res = await axios.post(`${API}/whatsapp-groups/join`, {
        name, phone, city: joinTarget.city,
      });
      setJoinResult(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to fetch group link. Please try again.';
      toast.error(msg);
    } finally {
      setJoinLoading(false);
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    if (!reqForm.name.trim() || !reqForm.phone.trim() || !reqForm.city.trim()) {
      toast.error('Please fill name, phone, and city');
      return;
    }
    setReqLoading(true);
    try {
      await axios.post(`${API}/whatsapp-groups/request`, reqForm);
      setReqSubmitted(true);
      toast.success('Request submitted! We\'ll notify you when a group is available.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setReqLoading(false);
    }
  };

  return (
    <div className="page-container" data-testid="community-page">
      <SEO
        title="Community - Elderra | Find WhatsApp Groups for Seniors"
        description="Join city-based WhatsApp groups for seniors. Connect with fellow elders in your city."
      />

      {/* Hero */}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-title" data-testid="community-title">Join the Elderra Community</h1>
          <p className="page-subtitle">
            Connect with fellow seniors in your city through our exclusive WhatsApp groups
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="community-overview">
        <div className="container">
          <div className="community-stats">
            <div className="community-stat">
              <div className="community-stat-number">50+</div>
              <div className="community-stat-label">Cities Covered</div>
            </div>
            <div className="community-stat">
              <div className="community-stat-number">1,500+</div>
              <div className="community-stat-label">Active Members</div>
            </div>
            <div className="community-stat">
              <div className="community-stat-number">100+</div>
              <div className="community-stat-label">Monthly Meetups</div>
            </div>
          </div>
        </div>
      </section>

      {/* Search */}
      <section className="search-section">
        <div className="container">
          <div className="search-container">
            <h2 className="search-title">Find Your City's WhatsApp Group</h2>
            <div className="search-box">
              <div className="search-input-wrapper">
                <Search className="search-icon" size={20} />
                <Input
                  type="text"
                  placeholder="Search by city name..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setDetectedCity(null); }}
                  className="search-input"
                  data-testid="city-search-input"
                />
                {detectedCity && (
                  <button className="loc-clear-btn" onClick={clearLocation} data-testid="clear-location-btn">
                    <X size={16} />
                  </button>
                )}
              </div>
              <Button
                onClick={handleLocationSearch}
                disabled={locationLoading}
                className="location-btn"
                data-testid="location-btn"
              >
                {locationLoading ? (
                  <><Loader2 size={18} className="dm-spin" /> Detecting...</>
                ) : (
                  <><Navigation size={18} /> Use My Location</>
                )}
              </Button>
            </div>

            {detectedCity && (
              <div className="loc-detected" data-testid="location-detected">
                <MapPin size={16} /> Showing groups near <strong>{detectedCity}</strong>
                {filteredGroups.length === 0 && (
                  <span className="loc-no-match"> — No groups found. Showing all groups below.</span>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Groups List */}
      <section className="groups-section">
        <div className="container">
          {loading ? (
            <div className="loading-state">Loading groups...</div>
          ) : (filteredGroups.length === 0 && !detectedCity) ? (
            <div className="empty-state">
              <MessageCircle size={64} className="empty-icon" />
              <h3>No groups found</h3>
              <p>We're expanding to more cities. Check back soon or contact us to request your city!</p>
            </div>
          ) : (
            <div className="groups-grid">
              {(filteredGroups.length > 0 ? filteredGroups : allGroups).map((group, index) => (
                <div key={index} className="group-card" data-testid={`group-card-${index}`}>
                  <div className="group-card-header">
                    <MessageCircle className="group-icon" size={32} />
                    <div className="group-badge">{group.region}</div>
                  </div>
                  <h3 className="group-name">{group.city}</h3>
                  <p className="group-state">{group.state}</p>
                  <div className="group-info">
                    <span className="group-members">{group.member_count} members</span>
                  </div>
                  <Button className="group-join-btn" data-testid={`join-group-${index}`} onClick={() => openJoinModal(group)}>
                    Join Group <ArrowRight size={16} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Request a Group */}
      <section className="req-group-section" data-testid="request-group-section">
        <div className="container">
          <div className="req-group-card">
            {reqSubmitted ? (
              <div className="req-success" data-testid="request-success">
                <div className="req-success-icon"><Check size={32} strokeWidth={3} /></div>
                <h3>Request Received!</h3>
                <p>We'll work on setting up a WhatsApp group for <strong>{reqForm.city}</strong> and notify you once it's ready.</p>
              </div>
            ) : (
              <>
                <div className="req-group-info">
                  <h2>Can't find your city?</h2>
                  <p>Request a WhatsApp group for your city and we'll set one up for you.</p>
                </div>
                <form className="req-group-form" onSubmit={handleRequestSubmit} data-testid="request-group-form">
                  <div className="req-form-row">
                    <Input data-testid="req-name-input" placeholder="Your Name" value={reqForm.name} onChange={e => setReqForm(f => ({ ...f, name: e.target.value }))} />
                    <Input data-testid="req-phone-input" type="tel" placeholder="Phone Number" value={reqForm.phone} onChange={e => setReqForm(f => ({ ...f, phone: e.target.value }))} />
                  </div>
                  <div className="req-form-row">
                    <Input data-testid="req-city-input" placeholder="Your City" value={reqForm.city} onChange={e => setReqForm(f => ({ ...f, city: e.target.value }))} />
                    <Input data-testid="req-state-input" placeholder="State (optional)" value={reqForm.state} onChange={e => setReqForm(f => ({ ...f, state: e.target.value }))} />
                  </div>
                  <Button type="submit" className="req-submit-btn" disabled={reqLoading} data-testid="req-submit-btn">
                    {reqLoading ? <><Loader2 size={18} className="dm-spin" /> Submitting...</> : <><Send size={18} /> Request a Group</>}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="community-benefits">
        <div className="container">
          <h2 className="section-title-center">What You Get in Our Community</h2>
          <div className="benefits-grid">
            <div className="benefit-card"><h3>Fraud Alerts</h3><p>Stay updated with the latest scam warnings and safety tips to protect yourself online.</p></div>
            <div className="benefit-card"><h3>Weekly Webinars</h3><p>Join online learning sessions covering various digital topics and tech trends.</p></div>
            <div className="benefit-card"><h3>Local Meetups</h3><p>Attend in-person gatherings in your city to learn, connect, and socialize.</p></div>
            <div className="benefit-card"><h3>Expert Moderation</h3><p>All groups are moderated by trained Elderra staff to ensure quality discussions.</p></div>
            <div className="benefit-card"><h3>Peer Support</h3><p>Connect with others facing similar challenges and share experiences.</p></div>
            <div className="benefit-card"><h3>Tech Help</h3><p>Get quick answers to your tech questions from the community and our team.</p></div>
          </div>
        </div>
      </section>

      {/* Meetups */}
      <section className="meetups-promo">
        <div className="container">
          <div className="meetups-promo-content">
            <h2>Join Our Community Meetups</h2>
            <p>4-hour programs designed to help elders navigate the digital world, stay safe, and meet new people.</p>
            <div className="meetups-features">
              <div className="meetup-feature"><span className="feature-check">✓</span><span>Comprehensive learning manual</span></div>
              <div className="meetup-feature"><span className="feature-check">✓</span><span>Hands-on app training</span></div>
              <div className="meetup-feature"><span className="feature-check">✓</span><span>Fraud prevention education</span></div>
              <div className="meetup-feature"><span className="feature-check">✓</span><span>Networking opportunities</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Join Group Modal */}
      <Dialog open={!!joinTarget} onOpenChange={closeJoinModal}>
        <DialogContent className="join-modal" data-testid="join-group-modal">
          <VisuallyHidden.Root>
            <DialogTitle>Join WhatsApp Group</DialogTitle>
            <DialogDescription>Enter your name and phone number to get the group link.</DialogDescription>
          </VisuallyHidden.Root>

          {joinResult ? (
            <div className="join-success" data-testid="join-success">
              <div className="join-success-icon"><Check size={36} strokeWidth={3} /></div>
              <h3>Welcome aboard!</h3>
              <p>Tap the button below to join the <strong>{joinResult.city}</strong> WhatsApp group.</p>
              <a
                href={joinResult.group_link}
                target="_blank"
                rel="noopener noreferrer"
                className="join-link-btn"
                data-testid="join-link-btn"
                onClick={() => setTimeout(() => closeJoinModal(false), 600)}
              >
                <MessageCircle size={18} /> Open WhatsApp Group <ExternalLink size={14} />
              </a>
              <button className="join-cancel-btn" onClick={() => closeJoinModal(false)} data-testid="join-modal-close">Close</button>
            </div>
          ) : (
            <div className="join-form-wrap">
              <div className="join-modal-header">
                <div className="join-modal-icon"><Users size={22} /></div>
                <h3>Join {joinTarget?.city} group</h3>
                <p>Enter your details to receive the group invite link.</p>
              </div>
              <form onSubmit={handleJoinSubmit} className="join-form">
                <div className="join-field">
                  <label>Your Name</label>
                  <Input
                    data-testid="join-name-input"
                    placeholder="e.g. Suresh Sharma"
                    value={joinForm.name}
                    onChange={(e) => setJoinForm({ ...joinForm, name: e.target.value })}
                    autoFocus
                    disabled={joinLoading}
                  />
                </div>
                <div className="join-field">
                  <label>Phone Number</label>
                  <Input
                    data-testid="join-phone-input"
                    type="tel"
                    placeholder="e.g. 98765 43210"
                    value={joinForm.phone}
                    onChange={(e) => setJoinForm({ ...joinForm, phone: e.target.value })}
                    disabled={joinLoading}
                  />
                </div>
                <Button
                  type="submit"
                  className="join-submit-btn"
                  disabled={joinLoading}
                  data-testid="join-submit-btn"
                >
                  {joinLoading ? <><Loader2 size={16} className="adm-spin" /> Getting link...</> : <>Get Group Link <ArrowRight size={16} /></>}
                </Button>
                <button type="button" className="join-cancel-btn" onClick={() => closeJoinModal(false)} data-testid="join-modal-cancel">Cancel</button>
                <p className="join-privacy">Your details help us welcome you to the right community. We never share your number.</p>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Community;
